import { useState, useMemo, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { KQL_QUERIES, GLOBAL_PARAMS, type KQLQuery } from '../data/kqlQueries';
import KQLCodeBlock from '../components/kql/KQLCodeBlock';
import { CalculatorContentWrapper } from '../components/shared/CalculatorContentWrapper';

// ── Parameter state ──────────────────────────────────────────────────────────

interface GlobalState {
  timeRange: string;
  costPerGB: string;
  costPerGBCustom: string;
  currencySymbol: string;
}

interface KQLRates {
  analyticsLogsGBP: number;
  sentinelGBP: number;
  basicLogsGBP: number;
  auxiliaryLogsGBP: number;
  commitmentTiers: Array<{ name: string; dailyCostGBP: number }>;
  lastUpdated: string | null;
  fallback?: boolean;
}

type QueryParamState = Record<number, Record<string, string>>;

function buildDefaultQueryParams(): QueryParamState {
  const state: QueryParamState = {};
  for (const q of KQL_QUERIES) {
    state[q.id] = {};
    for (const p of q.additionalParams ?? []) {
      state[q.id][p.id] = p.defaultValue;
    }
  }
  return state;
}

// ── Template substitution ────────────────────────────────────────────────────

function substituteTemplate(
  template: string,
  global: GlobalState,
  queryParams: Record<string, string>,
  query: KQLQuery,
): string {
  const costPerGB = global.costPerGB === 'custom' ? global.costPerGBCustom || '2.1339' : global.costPerGB;

  let result = template
    .replace(/\{\{TimeRange\}\}/g, global.timeRange)
    .replace(/\{\{CostPerGB\}\}/g, costPerGB)
    .replace(/\{\{CurrencySymbol\}\}/g, global.currencySymbol);

  // Prepend rate source comment if not already present
  if (!result.startsWith('// Rate:')) {
    result = `// Rate: £${costPerGB}/GB (UK South, updated nightly from Azure Retail Prices API)\n` + result;
  }

  for (const p of query.additionalParams ?? []) {
    const val = queryParams[p.id] ?? p.defaultValue;
    const resolved = val === 'custom' ? '' : val;
    result = result.replace(new RegExp(`\\{\\{${p.id}\\}\\}`, 'g'), resolved);
  }

  // Handle optional RGFilter injection
  if (query.id === 1) {
    const rgf = queryParams['RGFilter'] ?? '';
    if (rgf.trim()) {
      result = result.replace(
        '| summarize BillableDataBytes = sum(BillableDataBytes) by ResourceGroup',
        `| where ResourceGroup contains "${rgf.trim()}"\n| summarize BillableDataBytes = sum(BillableDataBytes) by ResourceGroup`,
      );
    }
  }

  // Handle optional TableFilter injection for query 6
  if (query.id === 6) {
    const tf = queryParams['TableFilter'] ?? '';
    if (tf.trim()) {
      result = result.replace(
        '| where IsBillable == true',
        `| where IsBillable == true\n| where DataType == "${tf.trim()}"`,
      );
    }
  }

  return result;
}

// ── Sub-components ───────────────────────────────────────────────────────────

function Select({ label, value, options, onChange }: {
  label: string;
  value: string;
  options: { label: string; value: string }[];
  onChange: (v: string) => void;
}) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-[11px] font-bold uppercase tracking-wide" style={{ color: 'var(--text-tertiary)' }}>{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="text-[13px] px-3 py-2 rounded focus:outline-none focus:border-[#0078d4] appearance-none cursor-pointer"
        style={{ backgroundColor: 'var(--surface-container-low)', border: '1px solid var(--outline-variant)', color: 'var(--text-secondary)' }}
      >
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}

function TextParam({ label, value, placeholder, onChange }: {
  label: string; value: string; placeholder?: string; onChange: (v: string) => void;
}) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-[11px] font-bold uppercase tracking-wide" style={{ color: 'var(--text-tertiary)' }}>{label}</label>
      <input
        type="text"
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="text-[13px] px-3 py-2 rounded focus:outline-none focus:border-[#0078d4]"
        style={{ backgroundColor: 'var(--surface-container-low)', border: '1px solid var(--outline-variant)', color: 'var(--text-secondary)' }}
      />
    </div>
  );
}

// ── Main page ────────────────────────────────────────────────────────────────

export default function KQLBuilderPage() {
  const [searchParams] = useSearchParams();
  const [selectedId, setSelectedId] = useState(1);
  const [selectedTag, setSelectedTag] = useState<string>('All');
  const [kqlRates, setKqlRates] = useState<KQLRates | null>(null);
  const [global, setGlobal] = useState<GlobalState>({
    timeRange: GLOBAL_PARAMS.timeRange.default,
    costPerGB: GLOBAL_PARAMS.costPerGB.default,
    costPerGBCustom: '2.1339',
    currencySymbol: GLOBAL_PARAMS.currencySymbol.default,
  });
  const [queryParams, setQueryParams] = useState<QueryParamState>(buildDefaultQueryParams);

  // Fetch live KQL rates on component mount
  useEffect(() => {
    const fetchRates = async () => {
      try {
        const resp = await fetch('/api/kql-rates');
        if (resp.ok) {
          const data = await resp.json() as KQLRates;
          setKqlRates(data);
          // Update default rate to live analytics logs rate
          setGlobal((prev) => ({
            ...prev,
            costPerGBCustom: data.analyticsLogsGBP.toFixed(4),
          }));
        }
      } catch {
        // Silently fall back to hardcoded default
      }
    };
    fetchRates();
  }, []);

  // Deep-link from Log Analytics calculator: ?rate=2.167 pre-fills the cost per GB
  useEffect(() => {
    const rateParam = searchParams.get('rate');
    if (!rateParam) return;
    const parsed = parseFloat(rateParam);
    if (!isNaN(parsed) && parsed >= 0.01 && parsed <= 50) {
      setGlobal((prev) => ({ ...prev, costPerGB: 'custom', costPerGBCustom: parsed.toFixed(3) }));
    }
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const selectedQuery = KQL_QUERIES.find((q) => q.id === selectedId)!;
  const currentQParams = queryParams[selectedId] ?? {};

  const substitutedCode = useMemo(
    () => substituteTemplate(selectedQuery.template, global, currentQParams, selectedQuery),
    [selectedQuery, global, currentQParams],
  );

  const explanationText = useMemo(
    () => selectedQuery.explanations.map((e) => `${e.label}: ${e.text}`).join('\n\n'),
    [selectedQuery],
  );

  const setQParam = (paramId: string, value: string) => {
    setQueryParams((prev) => ({
      ...prev,
      [selectedId]: { ...prev[selectedId], [paramId]: value },
    }));
  };

  const allTags = useMemo(() => {
    const tags = new Set<string>();
    KQL_QUERIES.forEach(q => q.tags?.forEach(t => tags.add(t)));
    return ['All', ...Array.from(tags).sort()];
  }, []);

  const filteredQueries = useMemo(() => {
    if (selectedTag === 'All') return KQL_QUERIES;
    return KQL_QUERIES.filter(q => q.tags?.includes(selectedTag));
  }, [selectedTag]);

  // If selected query is filtered out, select the first visible one
  useMemo(() => {
    if (filteredQueries.length > 0 && !filteredQueries.find(q => q.id === selectedId)) {
      setSelectedId(filteredQueries[0].id);
    }
  }, [filteredQueries, selectedId]);

  const costPerGBValue = global.costPerGB === 'custom' ? global.costPerGBCustom : global.costPerGB;

  return (
    <>
      {/* JSON-LD + Helmet */}
      <Helmet>
        <title>Azure Log Analytics KQL Query Builder | AzureCalc.uk</title>
        <meta name="description" content="Free KQL query builder for Azure Log Analytics cost analysis. 10 ready-to-use queries with customisable parameters for UK South pricing." />
        <link rel="canonical" href="https://www.azure-calc.co.uk/kql-builder" />
        <meta property="og:title" content="Azure KQL Cost Query Builder" />
        <meta property="og:description" content="Ready-to-use KQL queries for Log Analytics cost analysis. Free, no sign-in required." />
        <meta property="og:url" content="https://www.azure-calc.co.uk/kql-builder" />
        <meta property="og:image" content="https://www.azure-calc.co.uk/og/azure-calc-og.png" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'WebApplication',
          name: 'Azure Log Analytics KQL Cost Query Builder',
          description: 'Free KQL query builder for Azure Log Analytics cost analysis. 10 ready-to-use queries with customisable parameters.',
          url: 'https://www.azure-calc.co.uk/kql-builder',
          applicationCategory: 'DeveloperApplication',
          operatingSystem: 'Web',
          offers: { '@type': 'Offer', price: '0', priceCurrency: 'GBP' },
        })}</script>
      </Helmet>

      <div 
        className="min-h-screen"
        style={{ backgroundColor: 'var(--bg-base)' }}
      >
        <div className="max-w-[1200px] mx-auto px-4 py-8">

          {/* Back link */}
          <Link 
            to="/" 
            className="inline-flex items-center gap-1 text-[12px] mb-6 transition-colors"
            style={{ color: 'var(--text-tertiary)' }}
            onMouseEnter={(e) => e.currentTarget.style.color = 'var(--text-secondary)'}
            onMouseLeave={(e) => e.currentTarget.style.color = 'var(--text-tertiary)'}
          >
            <span className="material-symbols-outlined text-[16px]">arrow_back</span>
            Back to calculators
          </Link>

          {/* Header */}
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-[18px]">🔧</span>
              <span className="text-[11px] font-bold uppercase tracking-widest text-[#0078d4]">KQL Tools</span>
            </div>
            <h1 className="text-[28px] font-bold leading-tight" style={{ color: 'var(--text-primary)' }}>KQL Cost Query Builder</h1>
            <p className="text-[14px] mt-1" style={{ color: 'var(--text-tertiary)' }}>
              Ready-to-use KQL queries for Azure Log Analytics cost analysis.
              Customise the parameters and copy directly into Log Analytics.
            </p>
          </div>

          {/* Back to ingestion model link — shown when arriving from Log Analytics deep-link */}
          {searchParams.get('rate') && (
            <div className="mb-6 p-4 rounded-lg flex items-center justify-between gap-4" style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)' }}>
              <div className="flex items-center gap-2 min-w-0">
                <span className="material-symbols-outlined text-primary text-[18px] shrink-0">monitor_heart</span>
                <p className="text-[12px] text-on-surface-variant leading-snug">
                  Rate pre-filled from your Log Analytics estimate (£{searchParams.get('rate')}/GB).
                  Want to model the full ingestion cost?
                </p>
              </div>
              <Link
                to="/log-analytics"
                className="shrink-0 text-[11px] font-mono uppercase tracking-widest text-primary hover:underline flex items-center gap-1"
              >
                Log Analytics calculator
                <span className="material-symbols-outlined text-[14px]">arrow_forward</span>
              </Link>
            </div>
          )}

          {/* Always-visible back link to Log Analytics */}
          {!searchParams.get('rate') && (
            <div className="mb-4 flex items-center gap-2">
              <Link
                to="/log-analytics"
                className="inline-flex items-center gap-1 text-[11px] font-mono uppercase tracking-widest transition-colors"
                style={{ color: 'var(--text-tertiary)' }}
                onMouseEnter={(e) => e.currentTarget.style.color = '#0078d4'}
                onMouseLeave={(e) => e.currentTarget.style.color = 'var(--text-tertiary)'}
              >
                <span className="material-symbols-outlined text-[14px]">monitor_heart</span>
                Model ingestion costs in Log Analytics calculator
              </Link>
            </div>
          )}

          {/* Info box */}
          <div className="rounded-lg p-4 mb-8 flex gap-3" style={{ backgroundColor: 'var(--surface-container-low)', border: '1px solid rgba(0, 120, 212, 0.3)' }}>
            <span className="text-[18px] shrink-0">ℹ️</span>
            <p className="text-[13px] leading-relaxed" style={{ color: 'var(--text-tertiary)' }}>
              These queries run in <strong style={{ color: 'var(--text-secondary)' }}>your own Azure Log Analytics workspace</strong>.
              Copy the query and paste it into <em>Azure Portal → Log Analytics → Logs</em> to run it against your data.
              AzureCalc.uk does not connect to your Azure subscription.
            </p>
          </div>

          {/* Compliance & Filter Tags */}
          <div className="flex gap-2 mb-4 overflow-x-auto pb-2 scrollbar-hide">
            {allTags.map((tag) => (
              <button
                key={tag}
                onClick={() => setSelectedTag(tag)}
                className={[
                  'px-3.5 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-wider transition-all whitespace-nowrap',
                  selectedTag === tag
                    ? 'bg-[#0078d4] text-white'
                    : 'hover:border-[#0078d4] hover:text-[#c8e0f4]'
                ].join(' ')}
                style={selectedTag === tag ? {} : { backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)', color: 'var(--text-tertiary)' }}
              >
                {tag}
              </button>
            ))}
          </div>

          {/* Query tab bar */}
          <div className="flex gap-2 flex-wrap mb-6">
            {filteredQueries.map((q) => (
              <button
                key={q.id}
                onClick={() => setSelectedId(q.id)}
                className={[
                  'px-3 py-1.5 rounded text-[12px] font-semibold transition-all border whitespace-nowrap',
                  selectedId === q.id
                    ? 'bg-[#0078d4] border-[#0078d4] text-white'
                    : 'hover:border-[#0078d4]/50 hover:text-[#0078d4]',
                ].join(' ')}
                style={selectedId === q.id ? {} : { backgroundColor: 'var(--surface-container)', borderColor: 'var(--outline-variant)', color: 'var(--text-tertiary)' }}
              >
                {q.id}. {q.title}
              </button>
            ))}
          </div>

          {/* Rates metadata note */}
          {kqlRates && kqlRates.lastUpdated && (
            <div className="mb-6 text-[11px] font-mono tracking-widest" style={{ color: 'var(--text-tertiary)' }}>
              Rates sourced from Azure Retail Prices API · Last updated: {new Date(kqlRates.lastUpdated).toLocaleDateString('en-GB', { year: 'numeric', month: 'short', day: '2-digit' })} · UK South · GBP
            </div>
          )}

          {/* Two-column layout */}
          <div className="flex flex-col lg:flex-row gap-6">

            {/* LEFT: Code block (60%) */}
            <div className="flex-[3] min-w-0">
              <div className="mb-3">
                <h2 className="text-[16px] font-bold" style={{ color: 'var(--text-primary)' }}>{selectedQuery.title}</h2>
                <p className="text-[13px] mt-0.5" style={{ color: 'var(--text-tertiary)' }}>{selectedQuery.description}</p>
              </div>

              <KQLCodeBlock
                code={substitutedCode}
                explanationText={explanationText}
              />

              {/* Explanations */}
              <div className="mt-4 flex flex-col gap-2">
                {selectedQuery.explanations.map((exp, i) => (
                  <div key={i} className="flex gap-3 border-l-4 border-[#0078d4] rounded-r-lg px-4 py-3" style={{ backgroundColor: 'var(--surface-container)' }}>
                    <div className="min-w-0">
                      <p className="text-[12px] font-semibold mb-0.5" style={{ color: '#c7e0f4' }}>{exp.label}</p>
                      <p className="text-[12px] leading-relaxed" style={{ color: 'var(--text-tertiary)' }}>{exp.text}</p>
                    </div>
                  </div>
                ))}
              </div>

              {selectedQuery.note && (
                <div className="mt-3 rounded-lg px-4 py-3" style={{ backgroundColor: 'var(--surface-container-lowest)', border: '1px solid rgba(232, 176, 0, 0.3)' }}>
                  <p className="text-[12px]" style={{ color: '#e8b000' }}>⚠️ {selectedQuery.note}</p>
                </div>
              )}

              {/* Open in Azure link */}
              <div className="mt-4">
                <a
                  href="https://portal.azure.com/#blade/Microsoft_Azure_Monitoring_Logs/DemoLogsBlade"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-[12px] text-[#0078d4] hover:underline"
                >
                  <span className="material-symbols-outlined text-[15px]">open_in_new</span>
                  Open Azure Log Analytics demo workspace
                </a>
              </div>
            </div>

            {/* RIGHT: Parameter controls (40%) */}
            <div className="flex-[2] min-w-0 lg:max-w-[380px]">
              <div className="sticky top-20 flex flex-col gap-4">

                {/* Global params */}
                <div className="rounded-lg p-4" style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)' }}>
                  <p className="text-[11px] font-bold uppercase tracking-wide mb-3" style={{ color: 'var(--text-tertiary)' }}>Global Parameters</p>
                  <div className="flex flex-col gap-3">

                    <Select
                      label="Time Range"
                      value={global.timeRange}
                      options={GLOBAL_PARAMS.timeRange.options}
                      onChange={(v) => setGlobal((g) => ({ ...g, timeRange: v }))}
                    />

                    <div>
                      <Select
                        label="Cost per GB (£/GB)"
                        value={global.costPerGB}
                        options={GLOBAL_PARAMS.costPerGB.options}
                        onChange={(v) => setGlobal((g) => ({ ...g, costPerGB: v }))}
                      />
                      {global.costPerGB === 'custom' && (
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={global.costPerGBCustom}
                          onChange={(e) => setGlobal((g) => ({ ...g, costPerGBCustom: e.target.value }))}
                          className="mt-1.5 w-full text-[13px] px-3 py-2 rounded focus:outline-none font-mono"
                          style={{ backgroundColor: 'var(--surface-container-low)', border: '1px solid #0078d4', color: 'var(--text-secondary)' }}
                          placeholder="Enter cost per GB"
                        />
                      )}
                    </div>

                    <Select
                      label="Currency Symbol"
                      value={global.currencySymbol}
                      options={GLOBAL_PARAMS.currencySymbol.options}
                      onChange={(v) => setGlobal((g) => ({ ...g, currencySymbol: v }))}
                    />
                  </div>
                </div>

                {/* Query-specific params */}
                {(selectedQuery.additionalParams ?? []).length > 0 && (
                  <div className="rounded-lg p-4" style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)' }}>
                    <p className="text-[11px] font-bold uppercase tracking-wide mb-3" style={{ color: 'var(--text-tertiary)' }}>
                      Query Parameters
                    </p>
                    <div className="flex flex-col gap-3">
                      {selectedQuery.additionalParams!.map((param) => {
                        const val = currentQParams[param.id] ?? param.defaultValue;
                        if (param.type === 'dropdown') {
                          return (
                            <div key={param.id}>
                              <Select
                                label={param.label + (param.optional ? ' (optional)' : '')}
                                value={val}
                                options={param.options!}
                                onChange={(v) => setQParam(param.id, v)}
                              />
                              {val === 'custom' && (
                                <input
                                  type="number"
                                  step="0.0001"
                                  min="0"
                                  onChange={(e) => setQParam(param.id, e.target.value)}
                                  className="mt-1.5 w-full text-[13px] px-3 py-2 rounded focus:outline-none font-mono"
                                  style={{ backgroundColor: 'var(--surface-container-low)', border: '1px solid #0078d4', color: 'var(--text-secondary)' }}
                                  placeholder="Enter custom value"
                                />
                              )}
                            </div>
                          );
                        }
                        return (
                          <TextParam
                            key={param.id}
                            label={param.label + (param.optional ? ' (optional)' : '')}
                            value={val}
                            placeholder={param.placeholder}
                            onChange={(v) => setQParam(param.id, v)}
                          />
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Current values summary */}
                <div className="rounded-lg p-4" style={{ backgroundColor: 'var(--surface-container-low)', border: '1px solid var(--surface-container-high)' }}>
                  <p className="text-[11px] font-bold uppercase tracking-wide mb-2" style={{ color: 'var(--text-tertiary)' }}>Active Values</p>
                  <div className="flex flex-col gap-1 font-mono text-[11px]">
                    <div className="flex justify-between">
                      <span style={{ color: 'var(--text-tertiary)' }}>TimeRange</span>
                      <span style={{ color: '#4fc1ff' }}>{global.timeRange}</span>
                    </div>
                    <div className="flex justify-between">
                      <span style={{ color: 'var(--text-tertiary)' }}>CostPerGB</span>
                      <span style={{ color: '#b5cea8' }}>{costPerGBValue}</span>
                    </div>
                    <div className="flex justify-between">
                      <span style={{ color: 'var(--text-tertiary)' }}>CurrencySymbol</span>
                      <span style={{ color: '#ce9178' }}>"{global.currencySymbol}"</span>
                    </div>
                    {(selectedQuery.additionalParams ?? []).map((p) => {
                      const val = currentQParams[p.id] ?? p.defaultValue;
                      if (!val || val === 'custom') return null;
                      return (
                        <div key={p.id} className="flex justify-between">
                          <span style={{ color: 'var(--text-tertiary)' }}>{p.id}</span>
                          <span style={{ color: '#4fc1ff' }}>{val}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Related calculator link */}
                <Link
                  to="/log-analytics"
                  className="flex items-center gap-2 px-4 py-3 rounded-lg hover:border-[#0078d4] hover:text-[#0078d4] transition-colors text-[13px]"
                  style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)', color: 'var(--text-tertiary)' }}
                >
                  <span className="material-symbols-outlined text-[#0078d4] text-[18px]">calculate</span>
                  Log Analytics Cost Calculator →
                </Link>

                <Link
                  to="/guided"
                  className="flex items-center gap-2 px-4 py-3 rounded-lg hover:border-[#0078d4] hover:text-[#0078d4] transition-colors text-[13px]"
                  style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)', color: 'var(--text-tertiary)' }}
                >
                  <span className="text-[16px]">✨</span>
                  Guided Cost Estimator →
                </Link>
              </div>
            </div>

          </div>

          <CalculatorContentWrapper
            eyebrow="Operational Guidance"
            title="How to use these KQL cost queries safely and interpret the results"
            intro={
              <>
                <p className="mb-3">
                  This page is more than a code snippet library. The queries exist to help teams
                  understand how Log Analytics usage translates into cost drivers such as ingestion,
                  table mix, noisy resources, and historic search behavior. The value is not just in
                  copying a query, but in knowing what question it answers and how to validate the
                  output before changing architecture or budget assumptions.
                </p>
                <p>
                  AzureCalc.uk does not connect to your subscription. The queries run in your own
                  workspace, which is useful from a security standpoint, but it also means you need to
                  interpret the results in the context of your own retention settings, access patterns,
                  and table design.
                </p>
              </>
            }
            sections={[
              {
                id: 'why-these-queries',
                title: 'Why these queries exist',
                body: (
                  <>
                    <p className="mb-3">
                      Each query is designed to answer a practical FinOps or operations question. Which
                      resource groups are generating the most billable data? Which tables are consuming
                      the budget? How does a selected time range change the estimated spend profile?
                      Those are decisions teams need when they are controlling telemetry growth rather
                      than merely inspecting logs.
                    </p>
                    <p>
                      The builder adds parameters so you can adapt each template without manually
                      rewriting KQL syntax every time. That makes it easier to compare scenarios while
                      still keeping the query logic transparent.
                    </p>
                  </>
                ),
              },
              {
                id: 'safe-usage',
                title: 'How to use the queries safely',
                body: (
                  <ul className="list-disc pl-5 space-y-2">
                    <li>Run them first in the right workspace and time range so you do not overgeneralise from the wrong dataset.</li>
                    <li>Check the selected pricing assumption before presenting a cost figure as a real forecast.</li>
                    <li>Validate any custom filters, especially resource group or table parameters, so you do not accidentally narrow away the main source of spend.</li>
                    <li>Treat the results as a decision aid, not a replacement for invoice review or Azure Cost Management.</li>
                    <li>Sanity-check unusually large or small outputs against known ingestion spikes, incidents, or onboarding events.</li>
                  </ul>
                ),
              },
              {
                id: 'interpreting-output',
                title: 'How to interpret what the output is telling you',
                body: (
                  <>
                    <p className="mb-3">
                      A high-cost table is not automatically a problem. It may reflect valuable
                      security or platform telemetry. The important question is whether the data volume
                      aligns with operational value. Likewise, a low-cost result is not automatically
                      healthy if it is caused by short retention or incomplete ingestion.
                    </p>
                    <p>
                      Good interpretation means comparing the query result with the service purpose. If
                      a single resource group dominates spend, ask whether that is expected. If a table
                      grows rapidly, determine whether the increase was caused by a feature rollout, a
                      noisy data source, or a temporary incident.
                    </p>
                  </>
                ),
              },
              {
                id: 'worked-example',
                title: 'Worked example: from noisy table to action',
                body: (
                  <>
                    <p className="mb-3">
                      Suppose a query shows that one table is responsible for a disproportionate share
                      of billable ingestion over the last 30 days. The next step is not to delete the
                      data blindly. It is to identify the source, determine whether the records are
                      operationally necessary, and then decide whether filtering, plan changes, or
                      retention adjustments are safer interventions.
                    </p>
                    <p>
                      Used properly, these queries support a measured workflow: identify the cost
                      driver, verify the technical reason, then make a targeted change rather than a
                      blanket cut.
                    </p>
                  </>
                ),
              },
              {
                id: 'where-this-fits',
                title: 'Where this fits in the wider AzureCalc workflow',
                body: (
                  <>
                    <p className="mb-3">
                      The KQL Builder complements the Log Analytics calculators rather than replacing
                      them. Use the calculators when you need a planning estimate and use these queries
                      when you need to validate what is happening inside a live workspace. Together they
                      give you both prospective and diagnostic views of observability cost.
                    </p>
                    <p>
                      That pairing is also important for content quality: this page is useful because it
                      explains why the templates matter, not because it pads out a code tool with
                      generic filler.
                    </p>
                  </>
                ),
              },
            ]}
            methodologyNote={
              <>
                <p className="mb-2">
                  The default cost assumptions on this page are aligned to the broader AzureCalc.uk
                  pricing methodology, but the final result still depends on your workspace, your
                  selected filters, and the Azure pricing context you apply. Use the methodology page
                  when you need provenance for the reference numbers shown here.
                </p>
                <Link to="/methodology" className="text-[#0078d4] hover:underline">
                  Review methodology and provenance →
                </Link>
              </>
            }
            relatedLinks={[
              { label: 'Log Analytics Calculator', to: '/log-analytics' },
              { label: 'Log Analytics Pricing Guide', to: '/guides/azure-log-analytics-pricing-uk' },
              { label: 'Search Job vs Restore', to: '/guides/log-analytics-search-job-vs-restore' },
              { label: 'FAQ', to: '/faq' },
            ]}
          />
        </div>
      </div>
    </>
  );
}
